import { createContext, useContext } from 'react'
import type { Session, User, AuthError } from '@supabase/supabase-js'

/**
 * Profile metadata captured at signup. Read server-side by
 * tg_create_user_profile (in raw_user_meta_data) to populate
 * user_profiles + auto-create the Personal organization.
 */
export interface SignUpMetadata {
  first_name: string
  last_name: string
  phone?: string
  /** Optional. When provided, becomes the Personal org's name. */
  company_name?: string
}

export interface SignUpResult {
  error: AuthError | null
  /**
   * True when Supabase returned a session immediately — meaning
   * email confirmation is disabled and the user is already signed in.
   * False when a confirmation email was queued (or when Supabase's
   * anti-enumeration blanked the response — same downstream UX).
   */
  autoSignedIn: boolean
}

export interface AuthContextValue {
  user: User | null
  session: Session | null
  /** True while we're bootstrapping from the persisted session at app start. */
  loading: boolean
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>
  signUp: (
    email: string,
    password: string,
    metadata: SignUpMetadata,
  ) => Promise<SignUpResult>
  /**
   * Send a passwordless "magic link" sign-in email for an EXISTING user.
   * shouldCreateUser is false, so an unknown email does not create an account;
   * the UI shows a generic confirmation regardless to avoid leaking which
   * emails are registered. Clicking the link signs the user in and lands on
   * `next` (a same-origin internal path, e.g. from /sign-in?next=…) or /account
   * when omitted/unsafe.
   */
  signInWithMagicLink: (
    email: string,
    next?: string,
  ) => Promise<{ error: AuthError | null }>
  signOut: () => Promise<void>
  /**
   * Trigger Supabase Auth to re-send the email-confirmation link for a
   * pending signup. No-op for accounts already confirmed.
   */
  resendVerification: (email: string) => Promise<{ error: AuthError | null }>
  /**
   * Trigger Supabase Auth to send a password-reset email. The link lands on
   * the public /reset-password page carrying a one-time token_hash; the token
   * is only exchanged (and a session created) when the user submits a new
   * password there.
   */
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>')
  }
  return ctx
}
